import { ChannelIndex, senderIndexSsrLogic } from '@components/pages/sender';
import { TZDate } from '@date-fns/tz';
import {
  GetChannelBySlugDocument,
  GetChannelProgramDocument,
  GetChannelShowtimeByEventIdDocument,
} from '@lib/graphql/generated';
import { extractSsrState } from '@lib/graphql/helpers/extractSsrState';
import { fetchChannels } from '@lib/graphql/helpers/fetchChannels';
import { createUrqlClient } from '@lib/graphql/urql';
import { decodeOptions } from '@src/utils/urlHelpers';
import { composeDateString } from '@utils/dateHelpers';
import { add, parse, set, sub } from 'date-fns';
import type { GetStaticPaths } from 'next';
import dynamic from 'next/dynamic';

const ChannelDetailsContent = dynamic(
  () => import('@components/organisms/Channel/ChannelDetailsContent').then((mod) => mod.ChannelDetailsContent),
  {
    ssr: true,
  },
);

function ChannelDetails({ data, entry, slug }) {
  if (slug === 'index') {
    return <ChannelIndex data={data} entry={entry} />;
  }
  return <ChannelDetailsContent data={data} />;
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export async function getStaticProps({ params }) {
  const { ssrQuery, ssrCache, ssrOptionalAuthQuery } = createUrqlClient();

  const options = decodeOptions(params?.slug);

  if (options.slug === 'index') {
    return await senderIndexSsrLogic();
  }

  const channels = await fetchChannels(options.params.token);

  const slugArray = options.slug?.split('/');
  const slug = slugArray?.[0];

  const activeChannel =
    channels.data.find((e) => e.slug === slug) || channels.data.find((e) => e.id === slug) || channels.data[0];

  // No channels means the API failed. Throwing keeps the last good ISR page
  // instead of caching a broken one.
  if (!activeChannel) {
    throw new Error('Sender page: channel list is empty, API unavailable');
  }

  const openEntrySlug = slugArray.length > 1 ? slugArray.slice(-1)?.[0] : null;
  const openEntryId = openEntrySlug ? openEntrySlug.split('-')?.pop() : null;

  let openEntry = null,
    metadata = null;

  try {
    await ssrOptionalAuthQuery({
      query: GetChannelBySlugDocument,
      variables: { slug: params?.slug?.[0] },
      authToken: options.params.token,
    });
  } catch (err) {
    console.error(err);
  }

  if (openEntryId) {
    try {
      const { data } = await ssrQuery({
        query: GetChannelShowtimeByEventIdDocument,
        variables: { id: openEntryId, includeChannel: true },
      });
      const channelShowtime = data?.channelShowtimeByEventId;

      openEntry = channelShowtime || null;
      metadata = channelShowtime?.metadata || metadata;
    } catch (err) {
      console.error(err);
    }
  }

  if (activeChannel) {
    const dateRegex = /^[0-9]{8}$/;

    const activeDay = dateRegex.test(params?.slug?.[1])
      ? new TZDate(parse(params?.slug?.[1], 'yyyyMMdd', new Date()), 'Europe/Vienna')
      : new TZDate(new Date(), 'Europe/Vienna');

    const from = set(activeDay, { hours: 6, minutes: 0, seconds: 0, milliseconds: 0 });
    const to = set(add(activeDay, { days: 1 }), { hours: 6, minutes: 0, seconds: 0, milliseconds: 0 });

    const dayTimes = [
      {
        from: composeDateString(sub(from, { days: 1 })),
        to: composeDateString(sub(to, { days: 1 })),
      },
      {
        from: composeDateString(from),
        to: composeDateString(to),
      },
      {
        from: composeDateString(add(from, { days: 1 })),
        to: composeDateString(add(to, { days: 1 })),
      },
    ];

    await Promise.all(
      dayTimes.map(async (e) => {
        await ssrQuery({
          query: GetChannelProgramDocument,
          variables: {
            amount: 100,
            id: activeChannel.id,
            from: e.from,
            to: e.to,
            includeImage: true,
          },
        });
      }),
    );
  }

  const data = {
    channels: channels.data,
    activeChannel,
    metadata,
    openEntry: openEntry || null,
    activeDay: params?.slug?.[1] || null,
  };

  return {
    props: {
      data,
      urqlState: extractSsrState(ssrCache),
    },
  };
}

export default ChannelDetails;
